'use client';

import InspirationalSmsDialogue from '@/components/InspirationalSmsDialogue';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { fetchYouTubeStats } from '@/components/common/smartguy-logo';
import { cn } from '@/lib/utils';
import { signOut, useSession } from 'next-auth/react';
import { ArrowRight, GraduationCap, Play, Sunrise } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';

const DAILY_MESSAGE_PLAYLIST_URL =
  'https://www.youtube.com/playlist?list=PLfhxxUq_8RG6SqC25cnN1cSmESxs0nzgX';
const YOUTUBE_CHANNEL_URL = 'https://www.youtube.com/@SmartGuyAcademy';
const MEMBER_POLLS_URL = 'https://www.youtube.com/@SMARTGUYAcademy/community';

const pathwayRowClass =
  'cursor-pointer group flex w-full items-center gap-3 rounded-2xl border border-gray-200 bg-white px-3 py-3 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/25 hover:shadow-md sm:gap-4 sm:px-4 sm:py-3.5';

function PathwayRowContent({ number, numberClass, eyebrow, title }) {
  return (
    <>
      <span
        className={cn(
          'flex h-11 w-10 shrink-0 items-center justify-center rounded-lg text-sm font-bold text-white',
          numberClass
        )}
      >
        {number}
      </span>
      <span className="min-w-0 flex-1">
        <span className="text-muted-foreground block text-[11px] leading-tight sm:text-xs">
          {eyebrow}
        </span>
        <span className="text-primary font-figtree mt-0.5 block text-[1.05rem] leading-snug font-bold sm:text-lg">
          {title}
        </span>
      </span>
      <ArrowRight className="text-primary size-5 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5 sm:size-6" />
    </>
  );
}

export default function HomeLanding({ dailyMessageUrl = DAILY_MESSAGE_PLAYLIST_URL }) {
  const { data: session } = useSession();
  const [visitorsCount, setVisitorsCount] = useState(null);
  const isConsumer = session?.user?.role === 'consumer';
  const videosUrl = isConsumer ? '/chat' : '/videos';
  const faithVideosUrl = isConsumer ? '/chat?category=faith' : '/videos';

  useEffect(() => {
    let isMounted = true;

    const loadStats = async () => {
      const data = await fetchYouTubeStats();
      if (isMounted && data !== null) {
        setVisitorsCount(data);
      }
    };

    loadStats();

    return () => {
      isMounted = false;
    };
  }, []);

  const navLinks = [
    { title: 'Videos', url: videosUrl },
    { title: 'Blogs', url: '/blogs' },
    { title: 'Music', url: '/blogs/smartguy-music' },
    { title: 'Merch', url: 'https://smartguystore.net/' },
    { title: 'Scripture', url: dailyMessageUrl },
    { title: 'OCIA/RCIA', url: '/blogs/30-day-faith-journey' },
  ];

  const pathways = [
    {
      number: '01',
      eyebrow: 'Everything in one place',
      title: 'Free Videos & Classes',
      href: '/pages/explore-more',
      numberClass: 'bg-primary/90',
    },
    {
      number: '02',
      eyebrow: "Begin each morning with God's Word",
      title: 'Daily Scripture',
      action: 'scripture',
      numberClass: 'bg-amber-400',
    },
    {
      number: '03',
      eyebrow: 'Exploring Christianity?',
      title: 'Begin with Why Jesus?',
      href: '/blogs/why-jesus-15-short-videos-that-could-change-your-life',
      numberClass: 'bg-cyan-500',
    },
    {
      number: '04',
      eyebrow: 'Programs for adults and kids',
      title: 'Online OCIA/RCIA',
      href: '/blogs/30-day-faith-journey',
      numberClass: 'bg-cyan-700',
    },
    {
      number: '05',
      eyebrow: 'Prayer, Scripture and Catholic life',
      title: 'Explore 100+ Faith Videos',
      href: '/blogs/faith-series',
      numberClass: 'bg-cyan-600',
    },
    {
      number: '06',
      eyebrow: 'Bring monastic peace into daily life',
      title: 'Monk at Home Program',
      href: '/blogs/how-to-live-like-a-monk-at-home',
      numberClass: 'bg-amber-400',
    },
    {
      number: '07',
      eyebrow: 'Help someone who needs hope',
      title: 'Share a Video — Save a Life',
      href: '/pages/share-a-video-save-a-life',
      numberClass: 'bg-emerald-500',
    },
  ];

  return (
    <section className="font-figtree mx-auto -mt-5 max-w-2xl bg-white">
      <div className="px-4 pt-10 pb-2 sm:px-6 sm:pt-4">
        <nav
          aria-label="Primary"
          className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 sm:gap-x-5"
        >
          {navLinks.map((link) => {
            const isExternal = /^https?:\/\//i.test(link.url);

            return (
              <Link
                key={link.title}
                href={link.url}
                target={isExternal ? '_blank' : undefined}
                rel={isExternal ? 'noopener noreferrer' : undefined}
                className="text-primary text-[0.8rem] font-semibold hover:underline sm:text-sm"
              >
                {link.title}
              </Link>
            );
          })}
        </nav>
      </div>

      <div className="relative -mx-5 max-w-2xl overflow-hidden bg-gradient-to-br from-[#024059] via-[#00536B] to-[#007B91] !p-0 sm:mx-0 sm:px-0">
        <div
          className={cn(
            'absolute -bottom-50 -left-52 size-[365px]',
            'rounded-full border border-cyan-200/10 bg-cyan-400/10'
          )}
        />

        <div
          className={cn(
            'absolute -top-56 -right-56 z-[999] size-[360px]',
            'rounded-full border-[3px] border-cyan-200/20'
          )}
        />

        <div className="relative mx-auto px-5 pt-8 pb-16 text-center sm:px-8 sm:pt-10 sm:pb-20">
          <p className="text-[11px] font-semibold tracking-[0.18em] text-amber-300 uppercase sm:text-xs">
            Free Catholic resources for everyday life
          </p>
          <h1 className="mt-3 font-serif text-4xl leading-[1.15] font-semibold text-white sm:text-5xl">
            Put the Faith
            <span className="mt-1 block text-amber-300">in Your Hands.</span>
          </h1>

          <div className="mt-2 flex flex-row items-center justify-center gap-1.5">
            <Link href="/catholics-speaks" className="text-center text-xl text-white underline">
              Catholics Speak
            </Link>
            <ArrowRight className="mt-1 size-6 text-amber-300" />
          </div>

          <div className="mx-auto mt-7 grid max-w-xl grid-cols-3 gap-2 border-t border-white/25 pt-5 text-white">
            <div className="px-1">
              {visitorsCount ? (
                <p className="text-lg font-bold sm:text-2xl">
                  {visitorsCount.toLocaleString('en-US')}
                </p>
              ) : (
                <Skeleton className="mx-auto h-7 w-24 bg-white/20 sm:h-8" />
              )}
              <p className="mt-1 text-[10px] leading-tight text-white/75 sm:text-xs">
                people strong
              </p>
            </div>
            <div className="border-x border-white/25 px-1">
              <Link
                href={YOUTUBE_CHANNEL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="block hover:opacity-90"
              >
                <p className="text-lg font-bold sm:text-2xl">1M+</p>
                <p className="mt-1 text-[10px] leading-tight text-white/75 underline-offset-2 hover:underline sm:text-xs">
                  YouTube subscribers
                </p>
              </Link>
            </div>
            <div className="px-1">
              <p className="text-lg font-bold sm:text-2xl">Free</p>
              <p className="mt-1 text-[10px] leading-tight text-white/75 sm:text-xs">
                videos never monetized
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10 mx-auto -mt-8 max-w-2xl px-4 sm:-mt-10 sm:px-6">
        <Link
          href={dailyMessageUrl}
          // target="_blank"
          // rel="noopener noreferrer"
          className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3.5 shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl"
        >
          <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
            <Sunrise className="size-6" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="text-muted-foreground block text-xs">A little faith for your day</span>
            <span className="text-primary font-figtree block text-lg font-bold">Daily Message</span>
          </span>
          <ArrowRight className="text-primary size-5 shrink-0" />
        </Link>
      </div>

      <div className="mx-auto max-w-2xl px-4 pt-8 pb-6 sm:px-6">
        <p className="text-primary text-[11px] font-semibold tracking-[0.16em] uppercase">
          Start wherever you are
        </p>
        <h2 className="text-primary mt-1 font-serif text-3xl leading-tight font-semibold sm:text-[2.15rem]">
          What would help you today?
        </h2>

        <div className="mt-5 flex flex-col gap-2.5">
          {pathways.map((item) => {
            if (item.action === 'scripture') {
              return (
                <InspirationalSmsDialogue key={item.number}>
                  <button type="button" className={pathwayRowClass}>
                    <PathwayRowContent {...item} />
                  </button>
                </InspirationalSmsDialogue>
              );
            }

            const isExternal = /^https?:\/\//i.test(item.href);

            return (
              <Link
                key={item.number}
                href={item.href}
                target={isExternal ? '_blank' : undefined}
                rel={isExternal ? 'noopener noreferrer' : undefined}
                className={pathwayRowClass}
              >
                <PathwayRowContent {...item} />
              </Link>
            );
          })}
        </div>

        {/* <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm">
          <Link
            href={MEMBER_POLLS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary font-semibold hover:underline"
          >
            Member Polls
          </Link>
          <Link href="/blogs" className="text-primary font-semibold hover:underline">
            Browse Blogs
          </Link>
          <Link
            href={videosUrl}
            className="text-primary inline-flex items-center gap-1 font-semibold hover:underline"
          >
            <Play className="size-3.5 fill-current" />
            All Videos
          </Link>
          <Link
            href="/blogs/30-day-faith-journey"
            className="text-primary inline-flex items-center gap-1 font-semibold hover:underline"
          >
            <GraduationCap className="size-3.5" />
            OCIA Classes
          </Link>
        </div> */}
      </div>

      {/*  <div className="bg-primary mt-2 py-4 text-center text-[11px] tracking-[0.12em] text-white uppercase sm:text-xs">
        <span className="font-bold">SmartGuy®</span>
        <span className="mx-2 text-white/50">|</span>
        <span className="text-white/85">One click. One share. One soul at a time.</span>
      </div> */}
    </section>
  );
}
